import { ethers } from "ethers";
import { useEffect, useState } from "react";
import { getOrchestratorBaseUrl } from "../magic-link/guestSession";
export function hasProtectedWalletAccess(backendStatus, whitelistStatus) {
    return backendStatus === "authorized" && whitelistStatus === "authorized";
}
/**
 * CONTRACT_ADDRESS is the storage contract, never a whitelist fallback.
 * The RPC URL is used only to identify an official Avalanche network; the
 * whitelist proof itself comes exclusively from the orchestrator.
 */
export function getWalletWhitelistConfig() {
    return {
        contractAddress: process.env.CONTRACT_ADDRESS_USER_WHITELIST,
        rpcUrl: process.env.AVALANCHE_RPC_URL,
        chainId: process.env.AVALANCHE_CHAIN_ID,
    };
}
const WHITELIST_TIMEOUT_MS = 8_000;
export async function checkWalletWhitelist(address) {
    if (!ethers.utils.isAddress(address)) {
        return { status: "unavailable", reason: "invalid_wallet" };
    }
    const { contractAddress, rpcUrl, chainId } = getWalletWhitelistConfig();
    if (!contractAddress || !rpcUrl) {
        return { status: "unavailable", reason: "missing_config" };
    }
    if (!ethers.utils.isAddress(contractAddress)) {
        return { status: "unavailable", reason: "malformed_config" };
    }
    try {
        const url = new URL(rpcUrl);
        if (url.protocol !== "https:" && url.protocol !== "http:") {
            return { status: "unavailable", reason: "malformed_config" };
        }
    }
    catch {
        return { status: "unavailable", reason: "malformed_config" };
    }
    // Never guess a chain for a custom RPC. A configured chain takes precedence
    // over URL inference, including when the configured value is malformed.
    const expectedChainId = chainId === undefined || chainId === ""
        ? rpcUrl === "https://api.avax.network/ext/bc/C/rpc" ? 43114
            : rpcUrl === "https://api.avax-test.network/ext/bc/C/rpc" ? 43113 : undefined
        : /^(0|[1-9]\d*)$/.test(chainId) ? Number(chainId) : undefined;
    if (expectedChainId !== 43113 && expectedChainId !== 43114) {
        return { status: "unavailable", reason: "malformed_config" };
    }
    const endpoint = expectedChainId === 43113 ? "dev" : "prod";
    const controller = new AbortController();
    let timer;
    try {
        // Race the whole operation (including response body parsing), not merely
        // fetch: test doubles and broken transports may ignore the abort signal.
        const timeout = new Promise((_, reject) => {
            timer = setTimeout(() => {
                controller.abort();
                reject(new Error("whitelist timeout"));
            }, WHITELIST_TIMEOUT_MS);
        });
        const request = async () => {
            const response = await fetch(`${getOrchestratorBaseUrl()}/wallet-whitelist/${endpoint}/check`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ walletAddress: address }),
                credentials: "omit",
                cache: "no-store",
                signal: controller.signal,
            });
            if (response.status !== 200) {
                return { status: "unavailable", reason: "network_error" };
            }
            let result;
            try {
                result = await response.json();
            }
            catch {
                return { status: "unavailable", reason: "malformed_response" };
            }
            if (!result || typeof result !== "object" || Array.isArray(result) ||
                Object.keys(result).length !== 4 ||
                !("status" in result) || (result.status !== "authorized" && result.status !== "denied") ||
                !("walletAddress" in result) || typeof result.walletAddress !== "string" ||
                !ethers.utils.isAddress(result.walletAddress) ||
                result.walletAddress !== result.walletAddress.toLowerCase() ||
                result.walletAddress !== address.toLowerCase() ||
                !("contractAddress" in result) || typeof result.contractAddress !== "string" ||
                !ethers.utils.isAddress(result.contractAddress) ||
                result.contractAddress !== result.contractAddress.toLowerCase() ||
                result.contractAddress !== contractAddress.toLowerCase() ||
                !("chainId" in result) || typeof result.chainId !== "number" ||
                !Number.isSafeInteger(result.chainId) || result.chainId !== expectedChainId) {
                return { status: "unavailable", reason: "malformed_response" };
            }
            return { status: result.status };
        };
        return await Promise.race([request(), timeout]);
    }
    catch {
        return { status: "unavailable", reason: "network_error" };
    }
    finally {
        if (timer !== undefined)
            clearTimeout(timer);
    }
}
export function isCurrentBackendAuthCompletion(startedAccount, startedVersion, liveAccount, liveVersion) {
    return startedAccount.toLowerCase() === liveAccount?.toLowerCase() && startedVersion === liveVersion;
}
export function useBackendAuthStatus(account, isLiveWalletConnected, walletSessionVersion = 0, backendStatus = "checking") {
    const authorizationKey = `${isLiveWalletConnected ? "live" : "offline"}:${account ?? ""}:${walletSessionVersion}`;
    const accountCanBeChecked = isLiveWalletConnected && !!account && ethers.utils.isAddress(account);
    const [result, setResult] = useState({
        key: authorizationKey,
        status: accountCanBeChecked ? backendStatus : "idle",
    });
    useEffect(() => {
        if (!accountCanBeChecked) {
            setResult({ key: authorizationKey, status: "idle" });
            return;
        }
        setResult({ key: authorizationKey, status: backendStatus });
    }, [accountCanBeChecked, authorizationKey, backendStatus]);
    // Never expose authorization that belongs to an older account/session even
    // during the render before this effect starts the replacement check.
    if (result.key !== authorizationKey) {
        return accountCanBeChecked ? "checking" : "idle";
    }
    return result.status;
}
/**
 * Account/session-bound presentation state for the contract result.  The
 * immediate key comparison prevents a previous wallet's authorized result
 * from rendering for the next wallet while React is starting the replacement
 * effect.
 */
export function useContractWhitelistStatus(account, isLiveWalletConnected, walletSessionVersion = 0, status = "checking") {
    const authorizationKey = `${isLiveWalletConnected ? "live" : "offline"}:${account ?? ""}:${walletSessionVersion}`;
    const accountCanBeChecked = isLiveWalletConnected && !!account && ethers.utils.isAddress(account);
    const [result, setResult] = useState({
        key: authorizationKey,
        status: accountCanBeChecked ? status : "idle",
    });
    useEffect(() => {
        if (!accountCanBeChecked) {
            setResult({ key: authorizationKey, status: "idle" });
            return;
        }
        setResult({ key: authorizationKey, status });
    }, [accountCanBeChecked, authorizationKey, status]);
    if (result.key !== authorizationKey) {
        return accountCanBeChecked ? "checking" : "idle";
    }
    return result.status;
}
// Compatibility export retained for old callers. New route guards use the
// explicit contract/backend names so the two proofs cannot be conflated.
export const useWalletWhitelist = useContractWhitelistStatus;
